/**
 * Dyskusja pod fakturą B2B — widoczna dla autora faktury, CEO i księgowości.
 *
 * Oznaczać (@) można wyłącznie osoby z tego samego kręgu — lista podpowiedzi
 * jest zawężona po stronie UI, a backend i tak odrzuca oznaczenie osoby bez
 * dostępu do faktury (400).
 */
import { useState } from "react";
import DOMPurify from "dompurify";
import { Loader2, MessageSquare, Send, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { RichTextEditor, type MentionSuggestion } from "@/components/ui/RichTextEditor";
import { useCommentDraft } from "@/hooks/useCommentDraft";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import {
  useAddB2BInvoiceComment,
  useB2BInvoiceComments,
  useDeleteB2BInvoiceComment,
} from "../hooks/useB2BInvoices";

interface Props {
  invoiceId: string;
  /** UUID pracownika, który wystawił fakturę — wolno go oznaczyć w wątku. */
  employeeId: string;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["start"],
  });
}

function CommentContent({ content, names }: { content: string; names: string[] }) {
  if (content.trimStart().startsWith("<")) {
    return (
      <div
        className="rich-text-content text-sm text-slate-700"
        dangerouslySetInnerHTML={{
          __html: highlightMentionsInHtml(sanitize(content), names),
        }}
      />
    );
  }
  return (
    <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
      {highlightMentions(content, names)}
    </p>
  );
}

export function B2BInvoiceDiscussion({ invoiceId, employeeId }: Props) {
  const currentUser = useAuthStore((s) => s.user);
  const { data: comments = [], isLoading } = useB2BInvoiceComments(invoiceId);
  const { mutateAsync: addComment, isPending: sending } = useAddB2BInvoiceComment(invoiceId);
  const removeComment = useDeleteB2BInvoiceComment(invoiceId);
  const { data: allUsers = [] } = useInternalUsers();

  const [draft, setDraft, clearDraft] = useCommentDraft(`comment-draft-b2b-${invoiceId}`);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Krąg wątku = autor faktury + CEO + księgowość (lustro backendowego
  // user_can_access_b2b_discussion).
  const mentionSuggestions: MentionSuggestion[] = allUsers
    .filter((u) => u.is_ceo || u.role === "accountant" || u.id === employeeId)
    .map((u) => ({ id: u.id, name: u.full_name, role: u.role, is_ceo: u.is_ceo }));
  const mentionNames = mentionSuggestions.map((u) => u.name);

  async function handleSend() {
    const content = draft.trim();
    if (!content) return;
    setError(null);
    try {
      await addComment({ content, mentionIds });
      clearDraft();
      setMentionIds([]);
    } catch {
      setError("Nie udało się dodać komentarza.");
    }
  }

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4">
      <h2 className="mb-1 flex items-center gap-2 text-sm font-medium">
        <MessageSquare className="h-4 w-4 text-[var(--color-primary)]" /> Dyskusja
      </h2>
      <p className="mb-3 pl-6 text-xs text-slate-400">
        Widoczna dla wystawcy faktury, CEO i księgowości. Wpisz @ aby kogoś oznaczyć.
      </p>

      <div className="flex flex-col gap-3">
        {isLoading && (
          <div className="flex justify-center py-3">
            <Spinner size="sm" />
          </div>
        )}

        {!isLoading && comments.length === 0 && (
          <p className="text-sm italic text-slate-400">Brak komentarzy. Napisz pierwszy.</p>
        )}

        {comments.map((c) => {
          const isOwn = currentUser?.id === c.author_id;
          const canDelete = isOwn || currentUser?.is_ceo === true;
          return (
            <div
              key={c.id}
              className="flex gap-3 rounded-r-lg border-l-4 border-[var(--color-primary)] bg-slate-50 px-3 py-2.5"
            >
              {c.author_photo_url ? (
                <img
                  src={c.author_photo_url}
                  alt={c.author_name}
                  className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs font-bold text-white">
                  {(c.author_name || "?")
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-semibold text-slate-800">{c.author_name}</span>
                  {isOwn && <span className="text-xs text-slate-400">Ty</span>}
                  <span className="ml-auto whitespace-nowrap text-xs text-slate-400">
                    {formatDate(c.created_at)}
                  </span>
                  {canDelete && (
                    <button
                      type="button"
                      title="Usuń komentarz"
                      onClick={() => {
                        if (confirm("Usunąć ten komentarz?")) removeComment.mutate(c.id);
                      }}
                      className="text-slate-300 hover:text-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <CommentContent content={c.content} names={mentionNames} />

                {c.mentions.length > 0 && (
                  <p className="mt-1 text-[11px] text-slate-400">
                    Oznaczeni: {c.mentions.map((m) => m.full_name).join(", ")}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {/* Composer */}
        <div className="mt-1 flex flex-col gap-2 border-t border-slate-100 pt-3">
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            disabled={sending}
            placeholder="Napisz komentarz… (wpisz @ aby oznaczyć)"
            mentionSuggestions={mentionSuggestions}
            onMentionIdsChange={setMentionIds}
          />
          {error && <p className="text-xs text-rose-600">{error}</p>}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !draft.trim()}
              className="flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-sm text-white transition-colors hover:bg-[var(--color-primary-hover)] disabled:opacity-40"
            >
              {sending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              Wyślij
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
